/**
 * CreateEventMessaging — wizard Messaging step.
 * Faithful port of the canonical step (screens-creation.jsx → CreateEventMessaging):
 *   A) Pick one ready-made flow from your library (searchable + filterable),
 *      with a READ-ONLY diagram preview of the selection.
 *   B) Toggle "Design a specific flow for this event" to open the SAME flow
 *      designer used to build Messaging Flows (EventFlowBuilder), editing a
 *      per-event custom flow built from your message templates.
 * Selection + custom flow persist into the creation store (messagingMode /
 * messagingFlowId / messagingFlow) and save with the event.
 */

import { useState, type ChangeEvent } from "react";
import { Btn, Card, ChoiceTile, Dropdown, Icon, Input, Pill, T, Toggle, tx } from "@v0y4ge/jrni-ui";
import { CreationShell } from "./CreationShell";
import { CreationStore, useCreationConfig } from "./store";
import { useFlows, FLOW_KIND_PILL, flowStats, flowTrigLabel } from "../../messaging/useFlows";
import { FB_TRIGGERS, fbDefaultFlow, type FBFlow } from "../../builder/fb-model";
import { EventFlowBuilder } from "../../builder/EventFlowBuilder";
import { useT } from "../../../lib/i18n";

const WIZ_WIDE = 1060;

export default function CreateEventMessaging() {
  const t = useT();
  const cfg = useCreationConfig();
  const flowsStore = useFlows();
  const flows = flowsStore.all();

  const mode = (cfg as { messagingMode?: string }).messagingMode === "custom" ? "custom" : "library";
  const selectedId = (cfg as { messagingFlowId?: string }).messagingFlowId || "";
  const rawCustom = (cfg as { messagingFlow?: FBFlow }).messagingFlow;
  const customFlow: FBFlow = rawCustom && Array.isArray(rawCustom.steps) ? rawCustom : fbDefaultFlow();

  const setMode = (m: string) => CreationStore.setField("messagingMode", m);
  const pickFlow = (id: string) => CreationStore.setField("messagingFlowId", id === selectedId ? "" : id);
  const setCustom = (next: FBFlow) => CreationStore.setField("messagingFlow", next);

  const [flowQuery, setFlowQuery] = useState("");
  const [flowKindF, setFlowKindF] = useState("all");
  const [flowTrigF, setFlowTrigF] = useState("all");

  const kindsAvail = [...new Set(flows.map((f) => f.eventKind).filter(Boolean))];
  const trigsAvail = [...new Set(flows.map((f) => f.trigger?.event).filter(Boolean))];
  const qn = flowQuery.trim().toLowerCase();
  const visibleFlows = flows.filter((f) => {
    if (flowKindF !== "all" && f.eventKind !== flowKindF) return false;
    if (flowTrigF !== "all" && f.trigger?.event !== flowTrigF) return false;
    if (qn && !`${f.name} ${f.desc}`.toLowerCase().includes(qn)) return false;
    return true;
  });
  const filtersActive = flowKindF !== "all" || flowTrigF !== "all" || !!qn;
  const libDisabled = mode === "custom";

  return (
    <CreationShell step={6}>
      <div style={{ flex: 1, width: "100%", maxWidth: WIZ_WIDE, margin: "0 auto", padding: "32px 24px 24px", display: "flex", flexDirection: "column", gap: 16, minHeight: 0 }}>
        <div style={{ flexShrink: 0 }}>
          <h2 style={{ ...tx.h2, fontWeight: 700 }}>{t("events.creationCreateEventMessaging.messaging", "Messaging")}</h2>
          <p style={{ ...tx.help, marginTop: 4, maxWidth: 640 }}>{t("events.creationCreateEventMessaging.chooseHowGuestsAreMessagedForThisEventPickAReadyMade", "Choose how guests are messaged for this event. Pick a ready-made flow from your library, or design one just for this event.")}</p>
        </div>

        {/* A — library flow picker */}
        <Card style={{ padding: 16, flexShrink: 0, opacity: libDisabled ? 0.5 : 1, pointerEvents: libDisabled ? "none" : "auto", transition: "opacity 120ms" }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{t("events.creationCreateEventMessaging.useAFlowFromYourLibrary", "Use a flow from your library")}</div>
            <div style={{ fontSize: 12, color: T.soft, marginTop: 2 }}>{t("events.creationCreateEventMessaging.oneFlowPerEventBuiltFromYourApprovedMessagesFlowsAreSet", "One flow per event, built from your approved messages. Flows are set up by an administrator.")}</div>
          </div>
          {flows.length > 0 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
              <Input full={false} size="sm" iconLeft={<Icon.search size={14} />} placeholder={t("events.creationCreateEventMessaging.searchFlows", "Search flows…")} value={flowQuery} onChange={(e: ChangeEvent<HTMLInputElement>) => setFlowQuery(e.target.value)} style={{ width: 240 }} />
              <Dropdown
                options={[{ value: "all", label: t("events.creationCreateEventMessaging.allEventTypes", "All event types") }, ...kindsAvail.map((k) => ({ value: String(k), label: FLOW_KIND_PILL[String(k)]?.label ?? String(k) }))]}
                value={flowKindF}
                onChange={(v) => setFlowKindF(v)}
                aria-label={t("events.creationCreateEventMessaging.filterByEventType", "Filter by event type")}
                style={{ gap: 0, width: 190, flexShrink: 0 }}
              />
              <Dropdown
                options={[{ value: "all", label: t("events.creationCreateEventMessaging.allTriggers", "All triggers") }, ...trigsAvail.map((tr) => ({ value: String(tr), label: flowTrigLabel(String(tr)) }))]}
                value={flowTrigF}
                onChange={(v) => setFlowTrigF(v)}
                aria-label={t("events.creationCreateEventMessaging.filterByTrigger", "Filter by trigger")}
                style={{ gap: 0, width: 190, flexShrink: 0 }}
              />
              {filtersActive ? <Btn variant="ghost" size="sm" onClick={() => { setFlowQuery(""); setFlowKindF("all"); setFlowTrigF("all"); }}>{t("common.actions.clear", "Clear")}</Btn> : null}
              <span style={{ fontSize: 12, color: T.soft, marginLeft: "auto" }}>{visibleFlows.length} {t("events.creationCreateEventMessaging.of", "of")}{" "}{flows.length}</span>
            </div>
          ) : null}
          {flows.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", border: `1px dashed ${T.n4}`, borderRadius: 9, color: T.soft, fontSize: 13 }}>{t("events.creationCreateEventMessaging.noSavedFlowsYetAnAdministratorCanBuildFlowsInTheMessaging", "No saved flows yet. An administrator can build flows in the Messaging library; or design a custom flow below.")}</div>
          ) : visibleFlows.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", border: `1px dashed ${T.n4}`, borderRadius: 9, color: T.soft, fontSize: 13 }}>{t("events.creationCreateEventMessaging.noFlowsMatchYourSearchOrFilters", "No flows match your search or filters.")}</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, maxHeight: 368, overflow: "auto" }}>
              {visibleFlows.map((f) => {
                const on = f.id === selectedId; const st = flowStats(f); const kind = FLOW_KIND_PILL[f.eventKind];
                return (
                  <ChoiceTile
                    key={f.id}
                    selected={on}
                    onSelect={() => pickFlow(f.id)}
                    style={{ minHeight: 116 }}
                    label={
                      <span style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        {f.name}
                        {kind ? <Pill tone={kind.tone}>{kind.label}</Pill> : null}
                      </span>
                    }
                    sublabel={
                      <>
                        <span style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>{f.desc}</span>
                        <span style={{ display: "block", marginTop: 5 }}>{t("events.creationCreateEventMessaging.starts", "Starts:")}{" "}{flowTrigLabel(f.trigger?.event)} · {st.steps} {t("events.creationCreateEventMessaging.steps", "steps ·")}{st.messages} {t("events.creationCreateEventMessaging.messages", "messages")}</span>
                      </>
                    }
                  />
                );
              })}
            </div>
          )}
        </Card>

        {/* Read-only diagram of the selected library flow */}
        {mode !== "custom" && selectedId ? (() => {
          const picked = flows.find((f) => f.id === selectedId);
          return picked ? (
            <div style={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: T.soft, margin: "2px 2px 8px" }}>{t("events.creationCreateEventMessaging.preview", "Preview ·")}{picked.name}</div>
              <EventFlowBuilder flow={{ trigger: picked.trigger, steps: picked.steps }} onFlow={() => {}} readOnly height={480} />
            </div>
          ) : null;
        })() : null}

        {/* B — design a per-event custom flow (opens the flow designer) */}
        <label onClick={() => setMode(mode === "custom" ? "library" : "custom")} style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", border: `1px solid ${mode === "custom" ? T.p2 : T.border}`, borderRadius: 10, background: mode === "custom" ? T.p4 : T.white, cursor: "pointer" }}>
          <Toggle on={mode === "custom"} onChange={(v: boolean) => setMode(v ? "custom" : "library")} />
          <span style={{ minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 13.5, fontWeight: 600, color: T.n1 }}>{t("events.creationCreateEventMessaging.designASpecificFlowForThisEvent", "Design a specific flow for this event")}</span>
            <span style={{ display: "block", fontSize: 12, color: T.soft }}>{t("events.creationCreateEventMessaging.turnsOffTheLibrarySelectionAboveAndOpensTheFlowDesignerFor", "Turns off the library selection above and opens the flow designer for this event only.")}</span>
          </span>
        </label>

        {mode === "custom" ? (
          <div style={{ flexShrink: 0, display: "flex", flexDirection: "column" }}>
            <EventFlowBuilder flow={customFlow} onFlow={setCustom} height={620} />
          </div>
        ) : null}
      </div>
    </CreationShell>
  );
}
